/* eslint-disable @typescript-eslint/no-namespace */
import type * as CSS from 'csstype';

export namespace Font {
  /** Font Variant Keys */
  export type VariantKeys = 'fonts' | 'fontSizes' | 'fontWeights' | 'lineHeights' | 'letterSpacings';

  /** Font Variants */
  export type Variants = {
    [Key in VariantKeys]: Key extends 'fonts'
      ? FontFamily
      : Key extends 'fontSizes'
        ? Font.Size.AliasScaleKeys
        : Key extends 'fontWeights'
          ? FontWeight
          : Key extends 'lineHeights'
            ? LineHeight
            : Key extends 'letterSpacings'
              ? LetterSpacing
              : never;
  };

  /** Font Weights */
  export interface FontWeight extends Pick<CSS.Properties, 'fontWeight'> {
    /** @returns `300` */
    fw300: CSS.Property.FontWeight;
    /** @returns `400` */
    fw400: CSS.Property.FontWeight;
    /** @returns `500` */
    fw500: CSS.Property.FontWeight;
    /** @returns `600` */
    fw600: CSS.Property.FontWeight;
    /** @returns `700` */
    fw700: CSS.Property.FontWeight;
    /** @returns `800` */
    fw800: CSS.Property.FontWeight;
    /** @returns `900` */
    fw900: CSS.Property.FontWeight;
  }

  /** Font Weights Keys */
  export type FontWeightKeys = keyof FontWeight;

  /** Line Heights */
  export interface LineHeight extends Pick<CSS.Properties, 'lineHeight'> {
    /** @returns `1` */
    lh1: CSS.Property.LineHeight;
    /** @returns `1.1` */
    lh1_1: CSS.Property.LineHeight;
    /** @returns `1.2` */
    lh1_2: CSS.Property.LineHeight;
    /** @returns `1.35` */
    lh1_35: CSS.Property.LineHeight;
    /** @returns `1.5` */
    lh1_5: CSS.Property.LineHeight;
    /** @returns `1.75` */
    lh1_75: CSS.Property.LineHeight;
  }

  /** Line Height Keys */
  export type LineHeightKeys = keyof LineHeight;

  /** Letter Spacing */
  export interface LetterSpacing extends Pick<CSS.Properties, 'letterSpacing'> {
    /** @returns `normal` */
    normal: CSS.Property.LetterSpacing;
  }

  /** Font Family */
  export type FontFamily = {
    /** @return 'Graphik, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif' */
    body: CSS.Property.FontFamily;
    /** @return 'Graphik, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif' */
    heading: CSS.Property.FontFamily;
    /** @return 'monospace' */
    monospace: CSS.Property.FontFamily;
    /** @return 'Graphik' */
    graphik: CSS.Property.FontFamily;
  };

  /** Font Family Keys */
  export type FontFamilyKeys = keyof FontFamily;

  export namespace Size {
    /** Font Size Variant Values */
    export type VariantValues = 8 | 10 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 64 | 80 | 100 | 120;

    /** Font Size Variant Keys */
    export type VariantKeys =
      | 'fs8' // 8
      | 'fs10' // 10
      | 'fs12' // 12
      | 'fs16' // 16
      | 'fs20' // 20
      | 'fs24' // 24
      | 'fs32' // 32
      | 'fs40' // 40
      | 'fs48' // 48
      | 'fs64' // 64
      | 'fs80' // 80
      | 'fs100' // 100
      | 'fs120'; // 120

    /** Font Size Variants */
    export type Variants = {
      [Key in VariantKeys]: number;
    };

    /** Font Size Alias Scale Keys */
    export type AliasScaleKeys = number[] & Partial<Record<VariantKeys, number>>;
  }
}

const fontWeights: Font.FontWeight = {
  fw300: 300,
  fw400: 400,
  fw500: 500,
  fw600: 600,
  fw700: 700,
  fw800: 800,
  fw900: 900,
};

const lineHeights: Font.LineHeight = {
  lh1: 1,
  lh1_1: 1.1,
  lh1_2: 1.2,
  lh1_35: 1.35,
  lh1_5: 1.5,
  lh1_75: 1.75,
};

const letterSpacings: Font.LetterSpacing = {
  normal: 'normal',
};

const fontFamily: Font.FontFamily = {
  body: 'Graphik, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif',
  heading: 'Graphik, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica Neue, Arial, sans-serif',
  monospace: 'monospace',
  graphik: 'Graphik',
};

export const fontSizes: Font.Size.AliasScaleKeys = [8, 10, 12, 16, 20, 24, 32, 40, 48, 64, 80, 100, 120];

export const fontSizeAliases: Font.Size.Variants = {
  /** @returns `8` */
  fs8: fontSizes[0],
  /** @returns `10` */
  fs10: fontSizes[1],
  /** @returns `12.8` */
  fs12: fontSizes[2],
  /** @returns `16` */
  fs16: fontSizes[3],
  /** @returns `20` */
  fs20: fontSizes[4],
  /** @returns `24` */
  fs24: fontSizes[5],
  /** @returns `32` */
  fs32: fontSizes[6],
  /** @returns `40` */
  fs40: fontSizes[7],
  /** @returns `48` */
  fs48: fontSizes[8],
  /** @returns `64` */
  fs64: fontSizes[9],
  /** @returns `80` */
  fs80: fontSizes[10],
  /** @returns `100` */
  fs100: fontSizes[11],
  /** @returns `120` */
  fs120: fontSizes[12],
};

Object.keys(fontSizeAliases).map(fontSize => {
  const baseFontSizeKey = fontSize as Font.Size.VariantKeys;

  fontSizes[baseFontSizeKey] = fontSizeAliases[baseFontSizeKey];
});

export const fontSizeVariants = Object.keys(fontSizeAliases) as Font.Size.VariantKeys[];

const font: Font.Variants = {
  fonts: fontFamily,
  fontSizes,
  fontWeights,
  lineHeights,
  letterSpacings,
};

export default font;
